import type {
  BaseRecord,
  CrudFilters,
  DataProvider,
  GetListParams,
  GetOneParams,
} from '@refinedev/core'

import {
  ordersGetReceivedCancellationRequest,
  ordersListReceivedCancellationRequests,
} from '@repo/sdk'

import { i18n } from '../shared/i18n'

import { handleSdkError } from './sdkErrorUtils'
import { getApiUrl } from '../lib/runtime-env'

function extractStatus(
  filters?: CrudFilters,
): 'REQUESTED' | 'APPROVED' | 'COMPLETED' | 'REJECTED' | undefined {
  if (!filters) return undefined
  for (const filter of filters) {
    if ('field' in filter && filter.field === 'status' && 'value' in filter) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- filter value is status string
      return filter.value as 'REQUESTED' | 'APPROVED' | 'COMPLETED' | 'REJECTED'
    }
  }
  return undefined
}

function notImplemented(): never {
  throw new Error(i18n.t('common.errors.notImplemented'))
}

export const cancellationListDataProvider: DataProvider = {
  getList: async <TData extends BaseRecord = BaseRecord>(params: GetListParams) => {
    try {
      const page = params.pagination?.currentPage ?? 1
      const pageSize = params.pagination?.pageSize ?? 20
      const status = extractStatus(params.filters)

      const response = await ordersListReceivedCancellationRequests({
        query: { page, pageSize, status, initiatedBy: 'BUYER' },
      })

      return {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- SDK response matches TData structure
        data: (response.data?.data ?? []) as unknown as TData[],
        total: response.data?.meta.totalItems ?? 0,
      }
    } catch (error_) {
      throw handleSdkError(error_)
    }
  },

  getOne: async <TData extends BaseRecord = BaseRecord>(params: GetOneParams) => {
    try {
      const cancellationId = String(params.id)

      const response = await ordersGetReceivedCancellationRequest({
        path: { cancellationId },
      })

      if (!response.data) {
        throw new Error(i18n.t('common.errors.detailNotFound'))
      }

      return {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- SDK response matches TData structure
        data: response.data as unknown as TData,
      }
    } catch (error_) {
      throw handleSdkError(error_)
    }
  },
  create: notImplemented,
  update: notImplemented,
  deleteOne: notImplemented,
  getApiUrl,
}
